import { useMemo } from 'react';

import { deserializeStay } from '@shared/utils-universal/guest-stay';
import { useLoaderData as useReactRouterLoaderData } from 'react-router';

import type { Stay, StaySerialized } from '@shared/types/utils';

type LoaderData = {
  props?: {
    stay?: StaySerialized;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type DeserializedLoaderData<T extends LoaderData> = T extends {
  props: { stay: StaySerialized };
}
  ? Omit<T, 'props'> & {
      props: Omit<T['props'], 'stay'> & { stay: Stay };
    }
  : T;

/*
 * In SSR, Date objects in loader data must be serialized to strings to avoid critical Hydration and timezone issues:
 *
 * 1. Hydration Mismatch: If Date objects are sent from server to client, the browser
 *    will deserialize them using the CLIENT'S local timezone. This causes:
 *    - Server renders with UTC/server timezone
 *    - Client hydrates with user's local timezone
 *    - React detects DOM mismatch → Hydration fails
 *
 * 2. Timezone Bugs: A date like "2024-03-15" could render as:
 *    - "March 15" on server (UTC)
 *    - "March 14" on client (if user is in PST, UTC-8)
 *    - This breaks the user experience and causes booking errors
 *
 * So we need to serialize dates as strings (e.g., "2024-03-15") in loaders, then deserialize
 * them consistently on the client side using this hook.
 */
export function useDeserializedLoaderData<
  T extends LoaderData,
>(): DeserializedLoaderData<T> {
  const loaderData = useReactRouterLoaderData<T>();

  return useMemo(() => {
    if (!loaderData) {
      return loaderData as DeserializedLoaderData<T>;
    }

    if (
      loaderData.props &&
      typeof loaderData.props === 'object' &&
      'stay' in loaderData.props &&
      loaderData.props.stay
    ) {
      return {
        ...loaderData,
        props: {
          ...loaderData.props,
          stay: deserializeStay(loaderData.props.stay),
        },
      } as DeserializedLoaderData<T>;
    }

    return loaderData as DeserializedLoaderData<T>;
  }, [loaderData]);
}
